import type { UserTableView, UserTableViewApiId } from '@/model/UserTableView.model.ts';

export const VIEW_NAME_MAX_LENGTH = 100;

export const MAX_VIEWS_PER_TABLE = 10;

export const MAX_PUBLISHED_VIEWS_PER_TABLE = 10;

const RESERVED_NAMES = new Set(['default']);

export const partitionViews = (
  views: Array<UserTableView>
): { published: Array<UserTableView>; personal: Array<UserTableView> } =>
  views.reduce<{ published: Array<UserTableView>; personal: Array<UserTableView> }>(
    (acc, view) => {
      acc[view.published ? 'published' : 'personal'].push(view);
      return acc;
    },
    { published: [], personal: [] }
  );

export const savingBlockedReason = (views: Array<UserTableView>, isTenantAdmin: boolean): string | undefined => {
  const { published, personal } = partitionViews(views);

  if (personal.length < MAX_VIEWS_PER_TABLE) {
    return undefined;
  }

  if (!isTenantAdmin) {
    return `Maximum of ${MAX_VIEWS_PER_TABLE} personal views per table`;
  }

  return published.length >= MAX_PUBLISHED_VIEWS_PER_TABLE
    ? `Maximum of ${MAX_VIEWS_PER_TABLE} private and ${MAX_PUBLISHED_VIEWS_PER_TABLE} published views per table`
    : undefined;
};

export const canEditView = (view: UserTableView, isTenantAdmin: boolean): boolean => !view.published || isTenantAdmin;

export const validateViewName = (
  value: string,
  views: Array<UserTableView>,
  published: boolean,
  excludeApiId?: UserTableViewApiId
): string | true => {
  const trimmed = value.trim();

  if (trimmed === '') {
    return 'Please enter a name for this view';
  }

  if (trimmed.length > VIEW_NAME_MAX_LENGTH) {
    return `Name must be ${VIEW_NAME_MAX_LENGTH} characters or fewer`;
  }

  if (RESERVED_NAMES.has(trimmed.toLowerCase())) {
    return 'This name is reserved';
  }

  const isTaken = views.some(
    (view) =>
      view.published === published && view.api_id !== excludeApiId && view.name.toLowerCase() === trimmed.toLowerCase()
  );
  if (isTaken) {
    return published ? 'A published view with this name already exists' : 'A view with this name already exists';
  }

  return true;
};
